import {RootLayout} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import React, {PropsWithChildren, useState} from 'react';
import {
    gonative_save_secret,
    isBiometryActivated,
    logoutNowEverywhere,
    selectUserIsAuthorizedFlag,
} from '~/entities/user';
import {useLocationState} from '~/library/navigation';
import {SessionExpired} from '~/processes/onboarding/ui/forgot-password/SessionExpired';
import {SetPassword} from '~/processes/onboarding/ui/forgot-password/SetPassword';
import {PasswordIsSet} from '~/processes/onboarding/ui/shared/PasswordIsSet';
import {useAppDispatch, useAppSelector} from '~/shared/store';
import {getSavePassHandler} from '~/shared/utils/savePass';
import {PhoneVerificationScreen} from './PhoneVerificationScreen';

const savePassHandler = getSavePassHandler();

export function ForgotPasswordPage() {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(selectUserIsAuthorizedFlag);

    const [step, setStep] = useLocationState('forgot-password-step', SignupStep, Steps.phoneVerificationStart);

    const [username, setUsername] = useState<string | null>(null);
    const [uuid, setUuid] = useState<string | null>(null);
    const [password, setPassword] = useState<string | null>(null);

    if (step === Steps.setPassword && uuid && username) {
        return (
            <ForgotPasswordLayout>
                <SetPassword
                    username={username}
                    uuid={uuid}
                    onPasswordSet={password => {
                        setPassword(password);
                        setStep(Steps.passwordIsSet, {replace: true});
                    }}
                    onSessionExpired={() => setStep(Steps.sessionsExpired, {replace: true})}
                />
            </ForgotPasswordLayout>
        );
    }

    if (step === Steps.sessionsExpired) {
        const handleStartAgain = () => {
            setUuid(null);
            setPassword(null);
            setStep(Steps.phoneVerificationStart, {replace: true});
        };
        return <SessionExpired onStartAgain={handleStartAgain} />;
    }

    if (step === Steps.passwordIsSet && password) {
        const onPasswordIsSet = () => {
            if (isBiometryActivated()) {
                savePassHandler.setPassword(password);
                gonative_save_secret();
            }
            setTimeout(() => {
                if (isAuthenticated) {
                    dispatch(logoutNowEverywhere());
                } else {
                    window.restartAppAt('/login');
                }
            }, 500);
        };
        return <PasswordIsSet buttonText={'Log in'} onButtonClick={onPasswordIsSet} onUnmount={onPasswordIsSet} />;
    }

    return (
        <RootLayout title={'Forgot password'}>
            <PhoneVerificationScreen
                onVerificationSuccess={({username, uuid}) => {
                    setUsername(username);
                    setUuid(uuid);
                    setStep(Steps.setPassword);
                }}
                isOTP={step === Steps.phoneVerificationOTP}
                goToOTP={() => setStep(Steps.phoneVerificationOTP)}
            />
        </RootLayout>
    );
}

function ForgotPasswordLayout({children, disableBack}: PropsWithChildren<{disableBack?: boolean}>) {
    return (
        <RootLayout title={'Forgot password'} navigateBack={disableBack ? false : undefined}>
            {children}
        </RootLayout>
    );
}

const Steps = {
    phoneVerificationStart: 'phone-verification-start',
    phoneVerificationOTP: 'phone-verification-otp',
    setPassword: 'set-password',
    passwordIsSet: 'password-is-set',
    sessionsExpired: 'sessions-expired',
} as const;
const SignupStep = Schema.Literal(...Object.values(Steps));
